import { nanoid } from 'nanoid';
import { parseSessionTimeout } from '../../lib/session-timeout';

/** Legacy unscoped keys — removed after migrating values to institution-scoped keys. */
export const LEGACY_SESSION_ID_KEY = 'capio-engage-session-id';
export const LEGACY_THREAD_ID_KEY = 'capio-engage-thread-id';
export const LAST_MESSAGE_AT_KEY = 'capio-engage-last-message-at';

export type InactiveForBucket = 'lt_1d' | '1_7d' | '7_30d' | '30d_plus';

export function sessionIdStorageKey(engageId: string): string {
  return `${engageId}.${LEGACY_SESSION_ID_KEY}`;
}

export function threadIdStorageKey(engageId: string): string {
  return `${engageId}.${LEGACY_THREAD_ID_KEY}`;
}

export function lastMessageAtStorageKey(engageId: string): string {
  return `${engageId}.${LAST_MESSAGE_AT_KEY}`;
}

export function persistSessionId(engageId: string, sessionId: string): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(sessionIdStorageKey(engageId), sessionId);
  } catch {
    // Ignore storage unavailability
  }
}

export function persistThreadId(engageId: string, threadId: string): void {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(threadIdStorageKey(engageId), threadId);
  } catch {
    // Ignore storage unavailability
  }
}

export function readLastMessageAt(engageId: string): number | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(lastMessageAtStorageKey(engageId));
    if (!raw) return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  } catch {
    return null;
  }
}

export function writeLastMessageAt(
  engageId: string,
  timestampMs = Date.now()
): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(
      lastMessageAtStorageKey(engageId),
      String(timestampMs)
    );
  } catch {
    // Ignore storage unavailability
  }
}

export function clearLastMessageAt(engageId: string): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(lastMessageAtStorageKey(engageId));
  } catch {
    // Ignore storage unavailability
  }
}

export function getInactiveForBucket(elapsedMs: number): InactiveForBucket {
  const oneDayMs = 24 * 60 * 60 * 1000;
  if (elapsedMs < oneDayMs) return 'lt_1d';
  if (elapsedMs < 7 * oneDayMs) return '1_7d';
  if (elapsedMs < 30 * oneDayMs) return '7_30d';
  return '30d_plus';
}

export function isSessionInactiveExpired(
  lastMessageAt: number | null,
  timeoutSetting: string | undefined,
  nowMs = Date.now()
): boolean {
  const parsed = timeoutSetting ? parseSessionTimeout(timeoutSetting) : null;
  // never, and a token this build cannot read, do not expire the conversation.
  // 90m is custom and expires after 90 minutes.
  if (!parsed || parsed.kind === 'never' || parsed.minutes == null)
    return false;
  if (lastMessageAt === null) return false;
  // If device clock is behind or equal to lastMessageAt, continue conversation
  if (nowMs <= lastMessageAt) return false;
  const thresholdMs = parsed.minutes * 60 * 1000;
  return nowMs - lastMessageAt >= thresholdMs;
}

/**
 * Returns the institution-scoped session id, minting one if needed.
 */
export function getOrSetSessionId(engageId: string): string {
  if (typeof window === 'undefined') return nanoid();
  try {
    const scopedKey = sessionIdStorageKey(engageId);
    const scoped = localStorage.getItem(scopedKey);
    if (scoped) return scoped;

    const legacy = localStorage.getItem(LEGACY_SESSION_ID_KEY);
    if (legacy !== null) {
      localStorage.setItem(scopedKey, legacy);
      localStorage.removeItem(LEGACY_SESSION_ID_KEY);
      return legacy;
    }

    const newSessionId = nanoid();
    localStorage.setItem(scopedKey, newSessionId);
    return newSessionId;
  } catch {
    return nanoid();
  }
}

/**
 * Returns the institution-scoped thread id, minting one if needed.
 */
export function getOrSetThreadId(engageId: string): string {
  if (typeof window === 'undefined') return nanoid();
  try {
    const scopedKey = threadIdStorageKey(engageId);
    const scoped = sessionStorage.getItem(scopedKey);
    if (scoped) return scoped;

    const legacy = sessionStorage.getItem(LEGACY_THREAD_ID_KEY);
    if (legacy !== null) {
      sessionStorage.setItem(scopedKey, legacy);
      sessionStorage.removeItem(LEGACY_THREAD_ID_KEY);
      return legacy;
    }

    const newThreadId = nanoid();
    sessionStorage.setItem(scopedKey, newThreadId);
    return newThreadId;
  } catch {
    return nanoid();
  }
}
